import { Component, Input, OnChanges } from '@angular/core';

export interface Segment {
  fullKey: string;
  key: string;
  value: any;
  type: undefined | string;
  description: string;
  expanded: boolean;
}

@Component({
  selector: 'json-viewer',
  templateUrl: './json-viewer.component.html',
  styleUrls: ['./json-viewer.component.scss'],
})
export class JsonViewerComponent implements OnChanges {
  @Input() json: any;
  @Input() currentDepth = -1;
  @Input() expanded = true;
  @Input() parent = true;
  @Input() depth = -1;

  @Input() searchText = '';
  @Input() pathPrefix: string = ''; // New
  @Input() highlightPaths: string[] = []; // Already added

  segments: Segment[] = [];

  ngOnChanges() {
    this.segments = [];

    // remove cycles
    this.json = this.deCycle(this.json);

    this.currentDepth++;

    if (typeof this.json === 'object' && this.json !== null) {
      this.json = this.sortObjectByKeys(this.json);
      Object.keys(this.json).forEach((key) => {
        this.segments.push(this.parseKeyValue(key, this.json[key]));
      });
    } else {
      this.segments.push(this.parseKeyValue(`(${typeof this.json})`, this.json));
    }
  }

  isExpandable(segment: Segment) {
    return segment.type === 'object' || segment.type === 'array';
  }

  isHighlightedKey(segment: Segment): boolean {
    return this.highlightPaths.includes(segment.fullKey ?? '');
  }

  private buildFullPath(prefix: string, key: string): string {
    if (/^\d+$/.test(key)) {
      return `${prefix}[${key}]`; // array index
    } else if (prefix.endsWith(']')) {
      return `${prefix}.${key}`; // array object field
    } else if (prefix) {
      return `${prefix}.${key}`;
    }
    return key;
  }

  toggle(segment: Segment) {
    if (this.isExpandable(segment)) {
      segment.expanded = !segment.expanded;
    }
  }

  private sortObjectByKeys(object: {}) {
    return Object.keys(object)
      .sort()
      .reduce((r, k) => ((r[k] = object[k]), r), {});
  }

  private parseKeyValue(key: any, value: any): Segment {
    const fullKey = this.buildFullPath(this.pathPrefix, key);

    const segment: Segment = {
      fullKey: fullKey,
      key: key,
      value: value,
      type: undefined,
      description: '' + value,
      expanded: this.isExpanded(),
    };

    switch (typeof segment.value) {
      case 'number': {
        segment.type = 'number';
        break;
      }
      case 'boolean': {
        segment.type = 'boolean';
        break;
      }
      case 'function': {
        segment.type = 'function';
        break;
      }
      case 'string': {
        segment.type = 'string';
        segment.description = '"' + segment.value + '"';
        break;
      }
      case 'undefined': {
        segment.type = 'undefined';
        segment.description = 'undefined';
        break;
      }
      case 'object': {
        // yea, null is object
        if (segment.value === null) {
          segment.type = 'null';
          segment.description = 'null';
        } else if (Array.isArray(segment.value)) {
          segment.type = 'array';
          segment.description =
            'Array[' + Object.keys(segment.value).length + '] ' + JSON.stringify(Object.values(segment.value));
        } else if (segment.value instanceof Date) {
          segment.type = 'date';
        } else {
          segment.type = 'object';
          segment.description = 'Object ' + JSON.stringify(segment.value);
        }
        break;
      }
    }

    return segment;
  }

  private isExpanded(): boolean {
    return this.expanded && !(this.depth > -1 && this.currentDepth >= this.depth);
  }

  // https://github.com/douglascrockford/JSON-js/blob/master/cycle.js
  private deCycle(object: any) {
    const objects = new WeakMap();
    return (function derez(value, path) {
      let old_path;
      let nu: any;

      if (
        typeof value === 'object' &&
        value !== null &&
        !(value instanceof Boolean) &&
        !(value instanceof Date) &&
        !(value instanceof Number) &&
        !(value instanceof RegExp) &&
        !(value instanceof String)
      ) {
        old_path = objects.get(value);
        if (old_path !== undefined) {
          return { $ref: old_path };
        }
        objects.set(value, path);

        if (Array.isArray(value)) {
          nu = [];
          Object.keys(value).forEach((key) => {
            nu[key] = derez(value[key], `${path}["${key}"]`);
          });
        } else {
          nu = {};
          Object.keys(value).forEach(function (name) {
            nu[name] = derez(value[name], path + '[' + JSON.stringify(name) + ']');
          });
        }
        return nu;
      }
      return value;
    })(object, '$');
  }
}
